import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { injectIntl } from 'react-intl';
import config from 'config';
import xhr from 'xhr';
import pify from 'pify';
import { Helmet } from 'react-helmet';

import Animator from '@starbucks-web/pattern-library/lib/components/animator';
import ContentColumn from '@starbucks-web/pattern-library/lib/components/content-column';
import FormContainer from '@starbucks-web/pattern-library/lib/components/form-container/';
import { useModalContext } from '@starbucks-web/pattern-library/lib/components/modal-provider';

import FormWithMessaging from 'shared/app/components/form-with-messaging';
import { parseXhrResponseOrThrow } from 'shared/app/utils/fetch-utils';
import {
  addNotification as addNotificationAction,
  configSelector,
} from 'shared/app/shell';
import {
  signalSignIn as signalSigninAction,
  signalSignInError as signalSignInErrorAction,
  signalSignInSuccess as signalSignInSuccessAction,
} from '../../state/action-creators';
import { accertifyDataCollectorIsEnabledSelector } from 'shared/app/state/selectors/accertify-data-collector';

import { currentCountryCodeSelector } from 'shared/app/state/selectors/locales';
import Header from '../header';
import SignInFields from '../signin-fields';
import StarbucksRewardsMoment from '../starbucks-rewards-moment';
import { getFraudReputation } from 'shared/app/utils/iovation';
import {
  trackAuthLoginClick,
  trackAuthLoginError,
  trackAuthLoginSuccess,
} from '../../state/track-event';
import {
  storeCredential,
  attemptAutoSignin,
} from 'shared/app/utils/credential-management';
import { getJoinUrl } from '../../utils/get-join-url';

import getReturnUrlFromWindow from 'shared/app/utils/get-return-url-from-window';
import { getWindow } from 'shared/app/utils/window';
import IS_BROWSER from 'shared/app/shell/utils/is-browser';
import { showEnterCodeOverlayForLogin as showEnterCodeOverlayForLoginAction } from 'shared/app/bundles/mfa/state/show-overlay-action-creators';
import { removeAllFlags } from 'shared/app/utils/local-storage-flags';

import { getAccertifyUab } from 'shared/app/utils/accertify';

import messages from './messages';
import styles from './styles.cssm';

const OAUTH_MFA_CHALLENGE_ERROR_CODE = config.get(
  'universal.oauthMfaChallengeErrorCode'
);
const promisedXhr = pify(xhr);
const fields = {
  username: {},
  password: {},
  rememberMe: {
    required: false,
    input: {
      checked: true,
    },
  },
};

const win = IS_BROWSER ? getWindow() : null;

/* eslint-disable max-statements */
export const SignIn = ({
  addNotification,
  intl: { formatMessage },
  market,
  showEnterCodeOverlayForLogin,
  signalSignIn,
  signalSignInError,
  signalSignInSuccess,
  updatedTermsOfUseUrl,
  accertifyDataCollectorIsEnabled,
}) => {
  const { openModal } = useModalContext();
  const [errorMessageId, setErrorMessageId] = useState(null);
  const [isFetching, setIsFetching] = useState(false);
  const returnUrlFromQueryString = getReturnUrlFromWindow();

  const formRef = useRef();

  const getRedirectUrl = () => {
    return returnUrlFromQueryString || '/account/for-you';
  };

  const checkForMFAChallenge = ({ error, username, password }) => {
    signalSignInError(error?.response?.body);
    setIsFetching(false);

    const code = error?.response?.body?.error;
    if (code === OAUTH_MFA_CHALLENGE_ERROR_CODE) {
      const {
        id: phoneId,
        phoneNumber,
        phoneSmsState,
      } = error?.response?.body?.mfaChallengeMethods ?? {};

      showEnterCodeOverlayForLogin({
        username,
        password,
        phoneId,
        phoneNumber,
        phoneSmsState,
        onCodeSuccess: () => {
          setIsFetching(false);
          win.location.href = getRedirectUrl();
        },
        openModal,
      });
    } else {
      throw error;
    }
  };

  // Unregister any zombie service workers from deprecated PWA installers.
  // This can be removed when we no longer see BFF requests from PWA installers running an old
  // version of the PWA.
  const unregisterServiceWorker = () => {
    if (window.navigator.serviceWorker) {
      navigator.serviceWorker.getRegistration().then((registration) => {
        if (registration) {
          registration.unregister();
        }
      });
    }
  };

  useEffect(() => {
    removeAllFlags();
    unregisterServiceWorker();
    getFraudReputation()
      .then((reputation) =>
        attemptAutoSignin({
          postData: { market, reputation },
          signinAction: signalSignIn,
        })
      )
      .then(() => {
        signalSignInSuccess();
        win.location.href = getRedirectUrl();
      })
      .catch((error) => {
        signalSignInError(error);
        const { data, requestBody } = error;
        if (data && requestBody) {
          checkForMFAChallenge({
            error: {
              response: {
                body: data,
              },
            },
            username: requestBody.username,
            password: requestBody.password,
          });
        }
      })
      .catch(() => {
        /* Swallow non-MFA errors since autoSignin is a progressive enhancement */
      });
  }, []);

  const handleJoinClick = () => {
    win.location.href = getJoinUrl(win.location.search);
  };

  const postForm = (data) => {
    return promisedXhr({
      url: `/apiproxy/v1/account/signin`,
      json: data,
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      method: 'POST',
    }).then(parseXhrResponseOrThrow);
  };

  const handleSubmitError = (error) => {
    const messageId = error?.response?.body?.messageId;

    trackAuthLoginError();

    if (messageId) {
      setErrorMessageId(messageId);
    } else {
      addNotification(formatMessage(messages.errorMessage), {
        autoClose: false,
        title: formatMessage(messages.errorHeading),
        type: 'error',
      });
    }
  };

  const handleSubmit = ({ username, password, rememberMe }) => {
    setErrorMessageId(null);
    setIsFetching(true);
    signalSignIn();
    trackAuthLoginClick();

    const uab = accertifyDataCollectorIsEnabled ? getAccertifyUab() : {};
    return getFraudReputation()
      .then((reputation) => ({
        username,
        password,
        rememberMe,
        market,
        transactionSubType: 'signin',
        reputation: { ...reputation, ...uab },
      }))
      .then(postForm)
      .then(() =>
        Promise.all([
          storeCredential({
            username,
            password,
          }).catch(() => {
            // swallow error for edge case browsers
            // like Electron running in the Window Teams desktop app
          }),
          trackAuthLoginSuccess(),
        ])
      )
      .then(() => {
        setIsFetching(false);
        signalSignInSuccess();
        win.location.href = getRedirectUrl();
      })
      .catch((error) =>
        checkForMFAChallenge({
          error,
          username,
          password,
        })
      )
      .catch(handleSubmitError);
  };

  return (
    <React.Fragment>
      <Helmet title={formatMessage(messages.title)} />
      <ContentColumn className="py4 md-py7" size="md">
        <Header />
      </ContentColumn>
      <Animator animation="fade">
        <ContentColumn
          className="mb7"
          innerClassName={`${styles.column}`}
          key="fields"
          size="md"
        >
          <div className="sm-pt0 md-p5">
            <FormContainer
              fields={fields}
              focusOnInvalidAfterAnimation
              focusOnSubmitError
              onSubmit={handleSubmit}
              ref={formRef}
            >
              <FormWithMessaging>
                <SignInFields
                  errorHeading={formatMessage(messages.errorHeading)}
                  errorMessage={formatMessage(messages.invalidCredentialsError)}
                  isFetching={isFetching}
                  showErrorMessage={Boolean(errorMessageId)}
                  updatedTermsOfUseUrl={updatedTermsOfUseUrl}
                />
              </FormWithMessaging>
            </FormContainer>
          </div>
        </ContentColumn>
        <StarbucksRewardsMoment key="joinNow" onJoinClick={handleJoinClick} />
      </Animator>
    </React.Fragment>
  );
};

const select = (state) => ({
  market: currentCountryCodeSelector(state),
  updatedTermsOfUseUrl: configSelector(state).updatedTermsOfUseUrl,
  accertifyDataCollectorIsEnabled:
    accertifyDataCollectorIsEnabledSelector(state),
});

const actions = {
  addNotification: addNotificationAction,
  signalSignIn: signalSigninAction,
  signalSignInError: signalSignInErrorAction,
  signalSignInSuccess: signalSignInSuccessAction,
  showEnterCodeOverlayForLogin: showEnterCodeOverlayForLoginAction,
};

export default injectIntl(connect(select, actions)(SignIn));
